import { TestMarke } from "@/components/TestMarke";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "@/components/common";
import { api } from "@/lib/api";
import { aktion as ablaufAktion } from "@/lib/ablauf";
import {
  Plus, FileText, ClipboardCheck, Receipt, Eye, FolderOpen,
} from "lucide-react";
import { anzahlungsRechnungen as anzRechnungen, anzahlungStand as anzStand, stationenBerechnen, StationenLeiste } from "@/lib/vorgangStationen";

// A-015b: Umschalter „Alle Projekte / nur geöffnetes Projekt“ (gemerkt; Speicher kann fehlen → try/catch)
const FILTER_SCHLUESSEL = "werkbank_dokumente_projektfilter";
const filterLesen = () => { try { return window.localStorage.getItem(FILTER_SCHLUESSEL) === "offen" ? "offen" : "alle"; } catch { return "alle"; } };
const filterMerken = (w) => { try { window.localStorage.setItem(FILTER_SCHLUESSEL, w); } catch { /* ohne Speicher: nur für diese Sitzung */ } };

/**
 * Zeigt alle Angebote, Auftragsbestaetigungen und Rechnungen eines Kunden.
 * Wird in der KundenModulPage UND in der ProjektWerkbank wiederverwendet.
 *
 * Props:
 *   customerId  (Pflicht)  Kunden-ID
 *   projektId   (optional) wenn gesetzt, wird sie an die "Neu"-URLs angehaengt
 *                          (`/quotes/new?customer=…&projekt_id=…`); der Beleg gehört dann fest
 *                          zu diesem Projekt (A-015). Jede Zeile zeigt ihr Projekt.
 */
// kompakt (Werkbank, Vorschlag BUT-0001): ab 1280 px schmale Tabellenzeilen mit festen Spaltenbreiten.
// Ohne die Angabe (z. B. Kundenmodul) bleibt alles wie bisher.
// offenesProjekt (A-015b, Werkbank): {id, titel} des geöffneten Projekts. Dann gelten die „+ Neu“-Knöpfe rechts im
// Projekt (links ausgeblendet, keine doppelten Wege) und der Umschalter „Alle Projekte / nur geöffnetes Projekt“ erscheint.
export const CustomerDocumentsPanel = ({ customerId, projektId = null, kompakt = false, offenesProjekt = null }) => {
  const navigate = useNavigate();
  const [docs, setDocs] = useState({ quotes: [], orders: [], invoices: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  // Stationen-Leiste (06.10.2026): nur Anzeige, aus vorhandenen Daten berechnet
  const [kunde, setKunde] = useState(null);
  const [termine, setTermine] = useState([]);
  const [projektTitel, setProjektTitel] = useState({}); // A-015: id → Titel
  const [projekteGeladen, setProjekteGeladen] = useState(false); // Kunde ohne Projekte → „Altbeleg“ statt „zuordnen“
  const [projektFilter, setProjektFilterRoh] = useState(filterLesen); // A-015b
  const setProjektFilter = (w) => { setProjektFilterRoh(w); filterMerken(w); };

  useEffect(() => {
    const load = async () => {
      try {
        const [q, o, i] = await Promise.all([
          api.get("/quotes").then(r => r.data.filter(x => x.customer_id === customerId)).catch(() => []),
          api.get("/orders").then(r => r.data.filter(x => x.customer_id === customerId)).catch(() => []),
          api.get("/invoices").then(r => r.data.filter(x => x.customer_id === customerId)).catch(() => []),
        ]);
        setDocs({ quotes: q, orders: o, invoices: i });
        api.get(`/modules/kunden/data/${customerId}`).then(r => setKunde(r.data)).catch(() => {});
        api.get("/module-projekte/", { params: { kunde_id: customerId } })
          .then(r => setProjektTitel(Object.fromEntries((r.data || []).map(p => [p.id, p.titel || "(ohne Titel)"])))).catch(() => {}).finally(() => setProjekteGeladen(true));
        api.get("/module-termine", { params: { kunde_id: customerId } }).then(r => setTermine(Array.isArray(r.data) ? r.data : [])).catch(() => {});
      } finally { setLoading(false); }
    };
    load();
  }, [customerId]);

  const projektSuffix = projektId ? `&projekt_id=${projektId}` : "";
  const newQuote = () => { ablaufAktion("Angebot erstellen", { kunde_id: customerId, projekt_id: projektId }); navigate(`/quotes/new?customer=${customerId}${projektSuffix}`); };
  const newInvoice = () => navigate(`/invoices/new?customer=${customerId}${projektSuffix}`);
  const newOrder = async () => {
    try {
      const res = await api.post(`/orders/blank-for-customer/${customerId}`, projektId ? { projekt_id: projektId } : {});
      toast.success("Auftragsbestätigung angelegt");
      navigate(`/orders/edit/${res.data.id}${projektId ? `?projekt_id=${projektId}` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler beim Anlegen"); }
  };
  const orderFromQuote = async (quoteId) => {
    try {
      const res = await api.post(`/orders/from-quote/${quoteId}`);
      toast.success("Auftragsbestätigung erstellt");
      navigate(`/orders/edit/${res.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };
  const invoiceFromOrder = async (orderId) => {
    try {
      const res = await api.post(`/invoices/from-order/${orderId}`);
      // Anzahlung (05.10.2026): Anzahlungsrechnungen dieser AB in der Schlussrechnung abziehen
      await api.post(`/invoices/${res.data.id}/anzahlungen-abziehen`)
        .catch(() => toast.error("Anzahlungen konnten nicht abgezogen werden – bitte Rechnung prüfen"));
      toast.success("Rechnung erstellt");
      navigate(`/invoices/edit/${res.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("de-DE") : "—";
  const fmtEur = (n) => (n || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

  const statusColor = (status) => ({
    draft: "bg-gray-100 text-gray-700",
    sent: "bg-blue-100 text-blue-700",
    accepted: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
    paid: "bg-green-100 text-green-700",
    overdue: "bg-red-100 text-red-700",
    open: "bg-amber-100 text-amber-700",
    cancelled: "bg-gray-100 text-gray-500",
  }[status] || "bg-gray-100 text-gray-700");

  // A-015b: nur Belege des geöffneten Projekts (Anzeige); Anzahlung/Stationen rechnen weiter mit allen Belegen
  const nurOffenes = !!offenesProjekt && projektFilter === "offen";
  const sichtbar = (d) => !nurOffenes || d.projekt_id === offenesProjekt.id;
  const liste = { quotes: docs.quotes.filter(sichtbar), orders: docs.orders.filter(sichtbar), invoices: docs.invoices.filter(sichtbar) };
  const total = liste.quotes.length + liste.orders.length + liste.invoices.length;
  // Belege ohne Projekt (alle, unabhängig vom Umschalter); Kunde ganz ohne Projekte → Altbelege
  const keineProjekte = projekteGeladen && Object.keys(projektTitel).length === 0;
  const ohneProjekt = [...docs.quotes, ...docs.orders, ...docs.invoices].filter(d => !d.projekt_id).length;
  const alleAnzahl = docs.quotes.length + docs.orders.length + docs.invoices.length;

  // ---- Anzahlung je AB (gleiche Rechnung wie im Server: Summe der Anzahlungsrechnungen) ----
  const anzahlungsRechnungen = (orderId) => anzRechnungen(docs.invoices, orderId);
  const anzahlungStand = (order) => anzStand(order, docs.invoices);
  const orderIds = new Set(docs.orders.map(o => o.id));
  // Anzahlungsrechnungen stehen eingerueckt unter ihrer AB, nicht noch einmal oben in der Liste
  const unterAB = (inv) => inv.art === "anzahlung" && orderIds.has(inv.order_id);

  // Stationen-Leiste (gemeinsam mit der Vorgangskette im Projekt, lib/vorgangStationen)
  const renderStationen = (order) => (
    <StationenLeiste stationen={stationenBerechnen({ kunde, termine, quotes: docs.quotes, invoices: docs.invoices, order })}
      testId={`stationen-${order.id}`} idSuffix={order.id} className="ml-8 mt-1 mb-1" />
  );

  const tabs = [
    { id: "all", label: "Alle", icon: FileText, count: total },
    { id: "quotes", label: "Angebote", icon: FileText, count: liste.quotes.length, color: "blue" },
    { id: "orders", label: "Aufträge", icon: ClipboardCheck, count: liste.orders.length, color: "purple" },
    { id: "invoices", label: "Rechnungen", icon: Receipt, count: liste.invoices.length, color: "green" },
  ];

  const renderDocRow = (doc, type) => {
    const isQuote = type === "quote", isOrder = type === "order";
    const number = doc.quote_number || doc.order_number || doc.invoice_number || (doc.invoice_number === "" ? "Entwurf" : doc.id?.slice(0, 8));  // A-023
    const editUrl = isQuote ? `/quotes/edit/${doc.id}` : isOrder ? `/orders/edit/${doc.id}` : `/invoices/edit/${doc.id}`;
    const Icon = isQuote ? FileText : isOrder ? ClipboardCheck : Receipt;
    const typeColor = isQuote ? "text-blue-600" : isOrder ? "text-purple-600" : "text-green-600";
    return (
      <div key={doc.id}>
      <div className={`flex items-center gap-3 p-2.5 border rounded-sm hover:bg-muted/30 transition-colors group ${kompakt ? "xl:gap-2 xl:px-2 xl:py-1" : ""}`} data-testid={`doc-row-${doc.id}`}>
        <Icon className={`w-5 h-5 flex-shrink-0 ${typeColor} ${kompakt ? "xl:w-4 xl:h-4" : ""}`} />
        <div className={`flex-1 min-w-0 grid grid-cols-4 gap-2 items-center ${kompakt ? "xl:grid-cols-[minmax(7.5rem,1fr)_4.5rem_5.5rem_6rem]" : ""}`}>
          <div className={`font-mono text-sm font-medium col-span-4 sm:col-span-1 flex flex-wrap items-center gap-y-1`}>
            {number}
            {doc.uebungsbeleg && <TestMarke className="ml-1.5" titel="Übung – zählt nirgends mit, jederzeit löschbar" />}
            {/* A-015b: Beleg ohne Projekt → Klick öffnet ihn mit Projektauswahl */}
            {!doc.projekt_id && keineProjekte && (
              <span className="ml-1.5 font-sans text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-700 border border-slate-300"
                title="Dieser Kunde hat noch kein Projekt. Der Beleg bleibt hier vollständig sichtbar. Zum Zuordnen zuerst rechts ein Projekt anlegen." data-testid={`badge-altbeleg-${doc.id}`}>
                Altbeleg · ohne Projekt
              </span>
            )}
            {!doc.projekt_id && !keineProjekte && (
              <button type="button" onClick={() => { ablaufAktion("Beleg Projekt zuordnen", { dokument_id: doc.id, dokument_art: type, kunde_id: customerId }); navigate(`${editUrl}?projekt_waehlen=1`); }}
                className="ml-1.5 font-sans text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-900 border border-amber-300 hover:bg-amber-100"
                title="Dieser Beleg gehört noch zu keinem Projekt. Klicken: Beleg öffnen und Projekt wählen." data-testid={`badge-ohne-projekt-${doc.id}`}>
                ohne Projekt – zuordnen
              </button>
            )}
            {doc.projekt_id && <span className="ml-1.5 font-sans text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-800 border border-emerald-200 max-w-[10rem] truncate inline-block align-middle" title={`Projekt: ${projektTitel[doc.projekt_id] || "?"}`} data-testid={`badge-projekt-${doc.id}`}>{projektTitel[doc.projekt_id] || "Projekt"}</span>}
            {doc.art === "anzahlung" && <span className={`ml-1.5 font-sans text-[10px] font-semibold px-1.5 py-0.5 rounded-sm bg-purple-100 text-purple-800 ${kompakt ? "xl:whitespace-nowrap" : ""}`} data-testid={`badge-anzahlungsrechnung-${doc.id}`}>Anzahlungsrechnung</span>}
            {isOrder && anzahlungStand(doc).vereinbart > 0 && (() => {
              const a = anzahlungStand(doc);
              return a.offen <= 0.005
                ? <span className={`ml-1.5 font-sans text-[11px] font-semibold px-1.5 py-0.5 rounded-sm bg-green-100 text-green-800`} data-testid={`badge-anzahlung-${doc.id}`}>Anzahlung {fmtEur(a.bezahlt)} bezahlt</span>
                : <span className={`ml-1.5 font-sans text-[11px] font-semibold px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-900`} data-testid={`badge-anzahlung-${doc.id}`}>Anzahlung offen {fmtEur(a.offen)}</span>;
            })()}
          </div>
          <div className="text-xs text-muted-foreground">{fmtDate(doc.created_at || doc.date)}</div>
          <Badge className={`${statusColor(doc.status)} text-xs w-fit`}>{doc.status || "—"}</Badge>
          <div className="text-sm font-mono text-right">{fmtEur(doc.total_gross ?? doc.total ?? doc.brutto ?? 0)}</div>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <button
            onClick={() => {
              const nochNichtVersendet = isQuote ? doc.status === "Entwurf" : doc.status === "Offen";
              if (!nochNichtVersendet) {
                const label = isQuote ? "Dieses Angebot" : isOrder ? "Diese Auftragsbestätigung" : "Diese Rechnung";
                const weiter = window.confirm(
                  `${label} wurde bereits versendet oder bearbeitet (Status: ${doc.status}). Möchten Sie es wirklich bearbeiten? Ein erneutes Bearbeiten kann dazu führen, dass eine veränderte Version erneut verschickt wird.`
                );
                if (!weiter) return;
              }
              ablaufAktion("Dokument öffnen", { dokument_id: doc.id, dokument_art: type, kunde_id: customerId });
              navigate(editUrl);
            }}
            className="p-1.5 hover:bg-primary/10 text-primary rounded-sm transition-colors"
            title="Öffnen / Bearbeiten"
            data-testid={`btn-open-${doc.id}`}
          >
            <Eye className="w-4 h-4" />
          </button>
          {isQuote && (
            <button
              onClick={() => orderFromQuote(doc.id)}
              className="p-1.5 hover:bg-purple-50 text-purple-600 rounded-sm transition-colors opacity-0 group-hover:opacity-100"
              title="Auftragsbestätigung daraus erstellen"
              data-testid={`btn-to-order-${doc.id}`}
            >
              <ClipboardCheck className="w-4 h-4" />
            </button>
          )}
          {isOrder && (
            <button
              onClick={() => invoiceFromOrder(doc.id)}
              className="p-1.5 hover:bg-green-50 text-green-600 rounded-sm transition-colors opacity-0 group-hover:opacity-100"
              title="Rechnung daraus erstellen"
              data-testid={`btn-to-invoice-${doc.id}`}
            >
              <Receipt className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      {/* Anzahlung (05.10.2026): Zahlungsvermerke der AB */}
      {isOrder && doc.zahlungsvermerke?.length > 0 && (
        <ul className="ml-8 mt-0.5 mb-1 text-[11px] text-muted-foreground space-y-0.5" data-testid={`vermerke-${doc.id}`}>
          {doc.zahlungsvermerke.map(v => <li key={v.id}>{fmtDate(v.datum)} – {v.text}</li>)}
        </ul>
      )}
      {isOrder && renderStationen(doc)}
      {/* Anzahlungsrechnungen eingerueckt direkt unter ihrer AB (06.10.2026) */}
      {isOrder && tab !== "invoices" && anzahlungsRechnungen(doc.id).length > 0 && (
        <div className="ml-6 pl-2 border-l-2 border-purple-200 space-y-1 mt-1" data-testid={`ab-anzahlungsrechnungen-${doc.id}`}>
          {anzahlungsRechnungen(doc.id).map(inv => renderDocRow(inv, "invoice"))}
        </div>
      )}
      </div>
    );
  };

  return (
    <div className={`mt-4 pt-4 border-t ${kompakt ? "xl:mt-0 xl:pt-0 xl:border-t-0" : ""}`} data-testid={`customer-docs-${customerId}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
          <FileText className="w-4 h-4" /> Dokumente &amp; Vorgänge {total > 0 && <Badge className="bg-slate-100 text-slate-700">{total}</Badge>}
          {ohneProjekt > 0 && (
            <span className={`normal-case tracking-normal font-normal text-[11px] px-1.5 py-0.5 rounded-sm border ${keineProjekte ? "bg-slate-100 text-slate-700 border-slate-300" : "bg-amber-50 text-amber-900 border-amber-300"}`}
              title={keineProjekte ? "Ältere Belege aus der Zeit vor den Projekten. Sie bleiben hier vollständig sichtbar." : "Diese Belege gehören noch zu keinem Projekt. Am Beleg auf „ohne Projekt – zuordnen“ klicken."}
              data-testid={`hinweis-ohne-projekt-${customerId}`}>
              {ohneProjekt} {keineProjekte ? (ohneProjekt === 1 ? "Altbeleg ohne Projekt" : "Altbelege ohne Projekt") : "ohne Projekt"}
            </span>
          )}
        </h4>
        {offenesProjekt ? (
          <div className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid={`hinweis-neu-im-projekt-${customerId}`}>
            <FolderOpen className="w-3.5 h-3.5 text-emerald-700" /> Neue Belege für „{offenesProjekt.titel}“: rechts im geöffneten Projekt.
          </div>
        ) : (
        <div className="flex flex-wrap gap-1.5">
          <button onClick={newQuote} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-sm bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200" data-testid={`btn-new-quote-${customerId}`}>
            <Plus className="w-3 h-3" /> Angebot
          </button>
          <button onClick={newOrder} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-sm bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200" data-testid={`btn-new-order-${customerId}`}>
            <Plus className="w-3 h-3" /> Auftragsbestätigung
          </button>
          <button onClick={newInvoice} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-sm bg-green-50 text-green-700 hover:bg-green-100 border border-green-200" data-testid={`btn-new-invoice-${customerId}`}>
            <Plus className="w-3 h-3" /> Rechnung
          </button>
        </div>
        )}
      </div>
      {offenesProjekt && alleAnzahl > 0 && (
        <div className="inline-flex mb-2 rounded-sm border overflow-hidden text-xs" role="group" aria-label="Belege filtern" data-testid={`projektfilter-${customerId}`}>
          {[["alle", "Alle Projekte"], ["offen", `Nur „${offenesProjekt.titel}“`]].map(([w, t]) => (
            <button key={w} type="button" onClick={() => setProjektFilter(w)} aria-pressed={projektFilter === w}
              className={`px-2.5 py-1 max-w-[14rem] truncate ${projektFilter === w ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
              data-testid={`projektfilter-${w}-${customerId}`}>{t}</button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-xs text-muted-foreground py-3">Lade Dokumente...</div>
      ) : total === 0 ? (
        nurOffenes && alleAnzahl > 0
          ? <div className="text-xs text-muted-foreground py-2" data-testid={`projektfilter-leer-${customerId}`}>Zu „{offenesProjekt.titel}“ gibt es noch keine Belege. „Alle Projekte“ zeigt die übrigen.</div>
          : null
      ) : (
        <>
          <div className="flex max-lg:flex-wrap gap-1 mb-3 border-b">{/* Handy: Reiter umbrechen (BUT-0003) */}
            {tabs.map(t => {
              const T = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                  data-testid={`tab-${t.id}-${customerId}`}
                >
                  <T className="w-3.5 h-3.5" /> {t.label} {t.count > 0 && <span className="text-[10px] px-1 rounded bg-slate-100 text-slate-600">{t.count}</span>}
                </button>
              );
            })}
          </div>

          <div className={`space-y-1.5 ${kompakt ? "xl:space-y-1" : ""}`}>
            {(tab === "all" || tab === "quotes") && liste.quotes.map(d => renderDocRow(d, "quote"))}
            {(tab === "all" || tab === "orders") && liste.orders.map(d => renderDocRow(d, "order"))}
            {tab === "all" && liste.invoices.filter(d => !unterAB(d) || !liste.orders.some(o => o.id === d.order_id)).map(d => renderDocRow(d, "invoice"))}
            {tab === "invoices" && liste.invoices.map(d => renderDocRow(d, "invoice"))}
          </div>
        </>
      )}
    </div>
  );
};


// ==================== CREATE DOC PROMPT (Empty State mit Auswahl) ====================
const CreateDocPrompt = ({ onQuote, onOrder, onInvoice }) => {
  const [choosing, setChoosing] = useState(false);

  if (!choosing) {
    return (
      <div className="text-center py-8 bg-gradient-to-br from-slate-50 to-blue-50/30 rounded-lg border-2 border-dashed border-slate-200" data-testid="empty-docs-prompt">
        <FileText className="w-10 h-10 mx-auto mb-3 text-primary/60" />
        <div className="text-sm font-medium text-slate-700 mb-1">Noch keine Dokumente für diesen Kunden</div>
        <div className="text-xs text-muted-foreground mb-4">Möchtest du ein Dokument anlegen?</div>
        <button
          onClick={() => setChoosing(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-sm text-sm font-medium hover:bg-primary/90 shadow-sm"
          data-testid="btn-prompt-create-doc"
        >
          <Plus className="w-4 h-4" /> Ja, Dokument anlegen
        </button>
      </div>
    );
  }

  return (
    <div className="py-6 bg-gradient-to-br from-slate-50 to-blue-50/30 rounded-lg border-2 border-dashed border-primary/30" data-testid="empty-docs-choose">
      <div className="text-center mb-5">
        <div className="text-sm font-semibold text-slate-800 mb-1">Was möchtest du anlegen?</div>
        <div className="text-xs text-muted-foreground">Du kannst jederzeit aus einem Angebot eine Auftragsbestätigung oder Rechnung ableiten.</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl mx-auto px-4">
        <button
          onClick={onQuote}
          className="group flex flex-col items-center gap-2 p-5 bg-card border-2 border-blue-200 dark:border-blue-900 rounded-lg hover:border-blue-400 hover:shadow-md hover:bg-blue-50 dark:hover:bg-blue-950/40 transition-all"
          data-testid="choose-quote"
        >
          <FileText className="w-8 h-8 text-blue-600 group-hover:scale-110 transition-transform" />
          <div className="text-sm font-semibold text-blue-900 dark:text-blue-200">Angebot</div>
          <div className="text-xs text-center text-muted-foreground leading-relaxed">Preisvorschlag an den Kunden. Standard-Startpunkt.</div>
        </button>
        <button
          onClick={onOrder}
          className="group flex flex-col items-center gap-2 p-5 bg-card border-2 border-purple-200 dark:border-purple-900 rounded-lg hover:border-purple-400 hover:shadow-md hover:bg-purple-50 dark:hover:bg-purple-950/40 transition-all"
          data-testid="choose-order"
        >
          <ClipboardCheck className="w-8 h-8 text-purple-600 group-hover:scale-110 transition-transform" />
          <div className="text-sm font-semibold text-purple-900 dark:text-purple-200">Auftragsbestätigung</div>
          <div className="text-xs text-center text-muted-foreground leading-relaxed">Bestätigt dem Kunden den Auftrag schriftlich.</div>
        </button>
        <button
          onClick={onInvoice}
          className="group flex flex-col items-center gap-2 p-5 bg-card border-2 border-green-200 dark:border-green-900 rounded-lg hover:border-green-400 hover:shadow-md hover:bg-green-50 dark:hover:bg-green-950/40 transition-all"
          data-testid="choose-invoice"
        >
          <Receipt className="w-8 h-8 text-green-600 group-hover:scale-110 transition-transform" />
          <div className="text-sm font-semibold text-green-900 dark:text-green-200">Rechnung</div>
          <div className="text-xs text-center text-muted-foreground leading-relaxed">Direkte Rechnung ohne vorherigen Auftrag.</div>
        </button>
      </div>
      <div className="text-center mt-4">
        <button onClick={() => setChoosing(false)} className="text-xs text-muted-foreground hover:text-foreground underline">
          Abbrechen
        </button>
      </div>
    </div>
  );
};

export default CustomerDocumentsPanel;
